import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { catchError, filter, forkJoin, from, map, Observable, of, switchMap, throwError, timer } from 'rxjs';

import type { ShareModel } from '~/app/shared/types/envestboard-api/shareModel';
import type { MockDocument, MockFundLine, MockReadDocument } from './inventories-upload-mock/inventories-upload-mock.data';
import type { VerifyIsinsResponseModel } from '~/app/shared/types/envestboard-api/verifyIsinsResponseModel';
import { environment } from '~/environments/environment';

/**
 * 🧪 MOCK DE RECETTE — `POST /portfolios/inventories/upload` sans appeler l'extraction (Langflow, traitement IA facturé).
 * `grep -rn "MOCK-INVENTORIES" src/` pour tout retirer.
 *
 * Seul cet endpoint est simulé : la réponse est construite depuis les documents préparés du jeu de test
 * (`inventories-upload-mock.data.ts`), rapprochés du VRAI référentiel testing (`verify-isins`, `GET /shares/{id}`) ;
 * `validate`, `calculate` et la création partent normalement.
 *
 * ⚠️ Éteint par défaut, et JAMAIS en production (`environment.production`). S'arme depuis la console, puis recharger :
 *
 *     localStorage.setItem('EB_MOCK_INVENTORIES', '1')              // armer
 *     localStorage.setItem('EB_MOCK_INVENTORIES_DELAY_MS', '3000')  // délai simulé (défaut 10 s)
 *     localStorage.removeItem('EB_MOCK_INVENTORIES')                // désarmer
 *
 * Un document hors jeu de test est REFUSÉ (erreur 400), jamais envoyé au vrai endpoint : c'est le coût à éviter.
 * Le journal console ne porte ni montant ni nom de fichier (Sentry capture `console.*` en breadcrumbs).
 */
const FLAG = 'EB_MOCK_INVENTORIES';
const DELAY_FLAG = 'EB_MOCK_INVENTORIES_DELAY_MS';
const DEFAULT_DELAY_MS = 10_000;

const readStorage = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        // Fenêtre privée, stockage bloqué : le mock reste éteint, jamais d'exception.
        return null;
    }
};

export const isInventoriesUploadMockEnabled = (): boolean => !environment.production && readStorage(FLAG) === '1';

const mockDelayMs = (): number => {
    const value = Number(readStorage(DELAY_FLAG));
    return Number.isFinite(value) && value >= 0 && readStorage(DELAY_FLAG) !== null ? value : DEFAULT_DELAY_MS;
};

const problem = (req: HttpRequest<unknown>, status: number, detail: string): Observable<never> =>
    throwError(() => new HttpErrorResponse({
        status,
        url: req.url,
        error: { title: 'Mock inventories/upload', status, detail, instance: '/api/v1/portfolios/inventories/upload' },
    }));

export const inventoriesUploadMockInterceptor: HttpInterceptorFn = (req, next) => {
    if (req.method !== 'POST' || !req.url.endsWith('/portfolios/inventories/upload') || !isInventoriesUploadMockEnabled()) {
        return next(req);
    }
    const files = req.body instanceof FormData
        ? req.body.getAll('files').filter((entry): entry is File => entry instanceof File)
        : [];

    // Délai AVANT tout travail, annulable : quitter le parcours (TD-108) ou « Précédent » coupe bien l'import simulé.
    return timer(mockDelayMs()).pipe(
        switchMap(() => from(import('./inventories-upload-mock/inventories-upload-mock.data'))),
        switchMap((data) => {
            const read = files.map((file) => ({ fileName: file.name, document: data.MOCK_DOCUMENTS[data.documentStemOf(file.name)] }));
            const unknown = read.filter(({ document }) => document === undefined);
            if (files.length === 0 || unknown.length > 0) {
                console.warn(`[MOCK-INVENTORIES] ${unknown.length} document(s) hors jeu de test : refusé(s), aucun appel à l'extraction.`);
                return problem(req, 400, 'Mock : document hors jeu de test (tests/data/jeu-de-test)');
            }
            const failing = read.map(({ document }) => document).find((document) => document && data.isMockDocumentError(document));
            if (failing && data.isMockDocumentError(failing)) {
                return problem(req, failing.error, failing.detail);
            }
            const documents: MockReadDocument[] = read.map(({ fileName, document }) => ({ fileName, document: document as MockDocument }));
            const isins = data.isinsToVerify(documents);
            return verify(next, isins).pipe(
                switchMap((verifications) => details(next, verifications).pipe(map((shares) => ({ verifications, shares })))),
                map(({ verifications, shares }) => {
                    const body = data.buildMockInventory(documents, verifications, shares);
                    logBuckets(documents, verifications, data.bucketOf);
                    return new HttpResponse({ status: 200, url: req.url, body }) as HttpEvent<unknown>;
                }),
            );
        }),
        catchError((error: unknown) => throwError(() => error instanceof HttpErrorResponse
            ? error
            : new HttpErrorResponse({ status: 500, url: req.url, error: { detail: 'Mock inventories/upload : chargement impossible' } }))),
    );
};

/** Rapprochement réel du référentiel (le même appel que l'ancien import Excel). */
function verify(next: HttpHandlerFn, isins: string[]): Observable<VerifyIsinsResponseModel[]> {
    if (isins.length === 0) return of([]);
    return next(new HttpRequest<unknown>('POST', '/shares/verify-isins', { isins }, { responseType: 'json' })).pipe(
        responseBody<VerifyIsinsResponseModel[]>(),
    );
}

/** Nom, devise et catégorie des fonds trouvés ; en cas d'échec, les valeurs lues dans le document font foi. */
function details(next: HttpHandlerFn, verifications: VerifyIsinsResponseModel[]): Observable<Map<number, ShareModel>> {
    const ids = [...new Set(verifications.filter((v) => v.shareExist && v.shareId).map((v) => v.shareId as number))];
    if (ids.length === 0) return of(new Map());
    return forkJoin(ids.map((id) => next(new HttpRequest<unknown>('GET', `/shares/${id}`, { responseType: 'json' })).pipe(
        responseBody<ShareModel>(),
        catchError(() => of(null)),
    ))).pipe(map((shares) => new Map(shares.filter((share): share is ShareModel => !!share?.id).map((share) => [share.id as number, share]))));
}

function responseBody<T>() {
    return (source: Observable<HttpEvent<unknown>>): Observable<T> => source.pipe(
        filter((event): event is HttpResponse<unknown> => event instanceof HttpResponse),
        map((event) => event.body as T),
    );
}

/** ISIN → catégorie de rapprochement, SANS montant : dit quels cas D01…D14 le référentiel testing permet vraiment. */
function logBuckets(
    documents: readonly MockReadDocument[],
    verifications: VerifyIsinsResponseModel[],
    bucketOf: (fund: MockFundLine, verification: VerifyIsinsResponseModel | undefined) => string,
): void {
    const byIsin = new Map(verifications.map((v) => [v.isin, v]));
    const rows = documents.flatMap(({ document }) => document.funds.map((fund) => ({
        isin: fund.isin ?? '(sans ISIN)',
        euroFund: !!fund.isEuroFund,
        bucket: bucketOf(fund, fund.isin ? byIsin.get(fund.isin) : undefined),
    })));
    console.info('[MOCK-INVENTORIES] réponse simulée, aucun appel à l\'extraction');
    console.table(rows);
}
